import {Axios} from '@asocial/fe-utils'
import {createSelector, createSlice, Dispatch, PayloadAction} from '@reduxjs/toolkit'
import {AxiosResponse} from 'axios'
import {useQuery} from 'react-query'
import {useSelector} from 'react-redux'
import {ChatRequest} from 'entities/activity/types'
import {TrustedUserInfo} from 'entities/profile'
import {CursorType, ErrorResponsesType} from 'shared/types'
import {ChatUserType} from '../types'
import {MessageType} from './messages'

export type DialogItemType = {
  interlocutor: ChatUserType
  unreadCount: number
  lastMessage: MessageType
  isPinned: boolean
  idLastReadMessage: string | null
}

export type DialogsType = {
  cursor: CursorType
  list: DialogItemType[]
  totalCount: number
  search: string
  searchCursor: CursorType
  searchList: DialogItemType[]
  searchTotalCount: number
  openDialog: DialogItemType | null
  mockDialog?: {
    user?: TrustedUserInfo
    chatRequest?: ChatRequest
  }
}

const initialState: DialogsType = {
  cursor: '',
  list: [],
  totalCount: 0,
  search: '',
  searchCursor: '',
  searchList: [],
  searchTotalCount: 0,
  openDialog: null,
  mockDialog: undefined,
}

export const dialogsModel = createSlice({
  name: 'dialogs',
  initialState,
  reducers: {
    addDialogList: (state, {payload}: PayloadAction<{cursor: string, data: DialogItemType[], totalCount: number}>) => {
      if (state.cursor === payload.cursor) return

      state.cursor = payload.cursor
      state.list.push(...payload.data)
      state.totalCount = payload.totalCount
    },
    addDialogSearchList: (state, {payload}: PayloadAction<{cursor: string, data: DialogItemType[], totalCount: number}>) => {
      state.searchCursor = payload.cursor
      state.searchList.push(...payload.data)
      state.searchTotalCount = payload.totalCount
    },
    orderDialogList: (state, {payload: dialog}: PayloadAction<DialogItemType>) => {
      state.list = state.list.filter((item) => item.interlocutor.id !== dialog.interlocutor.id)

      // Если диалог запинен, то закидываем его в начало
      if (dialog.isPinned) {
        state.list.unshift(dialog)
        return
      }

      // Иначе закидываем в начало незапининых диалогов
      const index = state.list.findIndex((item) => !item.isPinned)
      index < 0
        ? state.list.push(dialog)
        : state.list.splice(index, 0, dialog)

      if (state.mockDialog?.user?.id === dialog.interlocutor.id) {
        state.openDialog = dialog
      }
    },
    setOpenDialog: (state, {payload}: PayloadAction<DialogItemType | null>) => {
      state.openDialog = payload
    },
    pinUnpinDialog: (state, {payload}: PayloadAction<{userId: string, isPinned: boolean}>) => {
      const {isPinned, userId} = payload

      if (state.openDialog) state.openDialog.isPinned = isPinned

      const dialog = state.list.find(({lastMessage}) => lastMessage.from.id === userId || lastMessage.to.id === userId)
      if (dialog) {
        dialog.isPinned = isPinned

        state.list = state.list.filter((item) => item.interlocutor.id !== dialog.interlocutor.id)

        const index = state.list.findIndex((item) => !item.isPinned)
        index < 0
          ? state.list.push(dialog)
          : state.list.splice(index, 0, dialog)
      }
    },
    setFilterDialogList: (state, {payload: search}: PayloadAction<string>) => {
      if (state.search === search) return

      state.searchCursor = ''
      state.searchList = []
      state.search = search
    },
    readDialog: (state, {payload}: PayloadAction<{idUser: string, newIdLastReadMessage: string, unreadCount: number}>) => {
      const {newIdLastReadMessage, idUser, unreadCount} = payload

      if (state.openDialog &&
        (state.openDialog.idLastReadMessage == null || newIdLastReadMessage > state.openDialog.idLastReadMessage)) {
        state.openDialog.idLastReadMessage = newIdLastReadMessage
        state.openDialog.unreadCount = unreadCount
      }

      const dialog = state.list.find(({interlocutor}) => interlocutor.id === idUser)
      if (dialog && (dialog.idLastReadMessage == null || newIdLastReadMessage > dialog.idLastReadMessage)) {
        dialog.idLastReadMessage = newIdLastReadMessage
        dialog.unreadCount = unreadCount
      }
    },
    setMockDialog: (state, {payload}: PayloadAction<TrustedUserInfo | undefined>) => {
      if (!payload) {
        state.mockDialog = undefined
        return
      }

      if (!state.mockDialog) state.mockDialog = {}
      state.mockDialog.user = payload
    },
    setMockDialogChatRequest: (state, {payload}: PayloadAction<ChatRequest>) => {
      if (!state.mockDialog) state.mockDialog = {}
      state.mockDialog.chatRequest = payload
    },
    clearDialogsList: (state) => {
      state.cursor = ''
      state.list = []
      state.totalCount = 0
    }
  }
})

export const {
  addDialogList,
  orderDialogList,
  setOpenDialog,
  setFilterDialogList,
  readDialog,
  addDialogSearchList,
  setMockDialog,
  setMockDialogChatRequest,
  clearDialogsList
} = dialogsModel.actions

export type GetDialogsResponse = AxiosResponse<{
  cursor: string
  data: DialogItemType[]
  totalCount: number
}>

export const GetDialogs = (dispatch: Dispatch) =>
  useQuery<GetDialogsResponse, ErrorResponsesType, GetDialogsResponse, [string, CursorType]>(
    ['chatDialogs', useSelector((state: RootState) => state.dialogs.cursor)],
    ({queryKey: [_, cursor]}) => Axios.get(
      'chat/dialogs',
      {params: {cursor: cursor || null}}
    ),
    {
      onSuccess: ({data}) => dispatch(addDialogList(data)),
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  )

export const GetDialogsSearch = (dispatch: Dispatch) =>
  useQuery<GetDialogsResponse, ErrorResponsesType, GetDialogsResponse, [string, DialogsType]>(
    ['chatDialogsSearch', useSelector((state: RootState) => state.dialogs)],
    ({queryKey: [_, {searchCursor, search}]}) => Axios.get(
      'chat/dialogs',
      {params: {cursor: searchCursor || null, search: search || null}}
    ),
    {
      onSuccess: ({data}) => dispatch(addDialogSearchList(data)),
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

export const GetDialog = (userId: string, dispatch: Dispatch) =>
  useQuery<AxiosResponse<DialogItemType | null>, ErrorResponsesType>(
    'chatDialog',
    () => Axios.get(`chat/dialogs/${userId}`),
    {
      onSuccess: ({data}) => data && dispatch(setOpenDialog(data)),
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  )

export const PinUnpinDialog = (userId: string, pin: boolean, dispatch: Dispatch) =>
  useQuery<AxiosResponse<DialogItemType>, ErrorResponsesType>(
    'pinDialog',
    () => Axios.patch(`chat/dialogs/${userId}/${pin ? 'pin' : 'unpin'}`),
    {
      onSuccess: () => dispatch(dialogsModel.actions.pinUnpinDialog({userId, isPinned: pin})),
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  )

export const useDialogsList = () => useSelector(
  createSelector(
    (state: RootState) => state.dialogs.list,
    (list) => list
  )
)

export const useDialogsSearchList = () => useSelector(
  createSelector(
    (state: RootState) => state.dialogs.searchList,
    (list) => list
  )
)

export const useOpenDialog = () => useSelector(
  createSelector(
    (state: RootState) => state.dialogs.openDialog,
    (openDialog) => openDialog
  )
)

export const useDialogsCursor = () => useSelector(
  createSelector(
    (state: RootState) => state.dialogs.cursor,
    (cursor) => cursor
  )
)

export const useDialogsSearchCursor = () => useSelector(
  createSelector(
    (state: RootState) => state.dialogs.searchCursor,
    (cursor) => cursor
  )
)

export const useDialogsSearch = () => useSelector(
  createSelector(
    (state: RootState) => state.dialogs.search,
    (search) => search
  )
)


export const useDialogsUnreadCount = () => useSelector(
  createSelector(
    (state: RootState) => state.dialogs.list,
    (list) => list.reduce((prev, curr) => prev + Number(curr.unreadCount), 0)
  )
)

export const useMockDialog = () => useSelector(
  createSelector(
    (state: RootState) => state.dialogs.mockDialog,
    (mockDialog) => mockDialog
  )
)

export const useDialogsTotalCount = () => useSelector(
  createSelector(
    (state: RootState) => state.dialogs.search,
    (state: RootState) => state.dialogs.totalCount,
    (state: RootState) => state.dialogs.searchTotalCount,
    (search, totalCount, searchTotalCount) => search ? searchTotalCount : totalCount
  )
)
